import { commonUtil } from '@hone-automation/common';
import { AskRequest } from '@hone-automation/common/lib/AskRequests';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

const isValidDate = (date: Date): boolean => !isNaN(date.getTime());

// compare two dates and return true if they are the same day. Doesn't take into account time
const checkDate = (date: Date, compareToDate: Date): boolean => {
  // Check if dates are valid
  if (!isValidDate(date) || !isValidDate(compareToDate)) {
    return false;
  }
  return (
    date.getDate() === compareToDate.getDate() &&
    date.getMonth() === compareToDate.getMonth() &&
    date.getFullYear() === compareToDate.getFullYear()
  );
};

export const getDaysAgo = (date: string): string | null => {
  // Convert the date string to a Date object
  if (!date) return null;
  const dateObj = new Date(date);
  if (!isValidDate(dateObj)) return null;

  const today = new Date();
  const diff = today.getTime() - dateObj.getTime();
  if (diff < 0) return '0 days ago'; // Handle future dates
  const daysAgo = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (daysAgo === 0) {
    const hoursAgo = Math.floor(diff / (1000 * 60 * 60));
    if (hoursAgo < 1) {
      const minutesAgo = Math.floor(diff / (1000 * 60));
      return `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`;
    }
    return `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
  }
  return daysAgo + ' day' + (daysAgo > 1 ? 's' : '') + ' ago';
};

// Coverts the date to a string in the format "Month Day, Year" or shortcuts like Today and Yesterday
export const evalDate = (date: string, timezone: string): string | null => {
  if (!date) return null;
  const utcDate = new Date(Date.parse(date));
  if (isNaN(utcDate.getTime())) return null;

  const zonedDate = toZonedTime(utcDate, timezone);
  const currentDate = toZonedTime(new Date(), timezone);

  const checkDate = (date1: Date, date2: Date): boolean => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  if (checkDate(zonedDate, currentDate)) {
    return `Today, ${formatInTimeZone(zonedDate, timezone, 'h:mm a')}`;
  }

  const yesterday = new Date(currentDate);
  yesterday.setDate(currentDate.getDate() - 1);

  if (checkDate(zonedDate, yesterday)) {
    return `Yesterday, ${formatInTimeZone(zonedDate, timezone, 'h:mm a')}`;
  }

  return formatInTimeZone(zonedDate, timezone, 'MMM dd, yyyy');
};

// If the date exists, it should return the date in the format 12/31/2023
// If the date does not exist, it should return null
export const shortDate = (date?: string): string | null => {
  if (!date) return null;
  const dateObj = new Date(date);
  if (!isValidDate(dateObj)) return null;
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export type GroupData = {
  label: string;
  date: Date;
  requests: AskRequest[];
};

// Group requests by date
export const getGroupedDates = (requests: AskRequest[], reversed: boolean = false) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const lastWeekStart = new Date(today);
  lastWeekStart.setDate(today.getDate() - 7);
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);

  const groups: { [key: string]: GroupData } = {
    today: {
      label: 'Today',
      date: today,
      requests: [],
    },
    yesterday: {
      label: 'Yesterday',
      date: yesterday,
      requests: [],
    },
    lastWeek: {
      label: 'Last Week',
      date: lastWeekStart,
      requests: [],
    },
    thisMonth: {
      label: 'This Month',
      date: thisMonthStart,
      requests: [],
    },
    lastMonth: {
      label: 'Last Month',
      date: lastMonthStart,
      requests: [],
    },
  };

  requests.forEach(request => {
    //should get date from request updatedAt state
    const date = new Date(request.updatedAt);

    if (date.toDateString() === today.toDateString()) {
      groups.today.requests.push(request);
    } else if (date.toDateString() === yesterday.toDateString()) {
      groups.yesterday.requests.push(request);
    } else if (date >= lastWeekStart) {
      groups.lastWeek.requests.push(request);
    } else if (
      date >= thisMonthStart &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      groups.thisMonth.requests.push(request);
    } else if (date >= lastMonthStart && date < thisMonthStart) {
      groups.lastMonth.requests.push(request);
    } else if (date.getFullYear() === today.getFullYear()) {
      // Current year months (excluding last month)
      const groupKey = `${today.getFullYear()}-${date.getMonth()}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          label: `${date.toLocaleString('default', { month: 'long' })} ${today.getFullYear().toString().slice(-2)}`,
          date: new Date(today.getFullYear(), date.getMonth(), 1),
          requests: [],
        };
      }
      groups[groupKey].requests.push(request);
    } else {
      // Previous years
      const yearKey = `year-${date.getFullYear()}`;
      if (!groups[yearKey]) {
        groups[yearKey] = {
          label: date.getFullYear().toString(),
          date: date, // Use the actual date instead of year end
          requests: [],
        };
      }
      groups[yearKey].requests.push(request);
    }
  });

  // Rest of the filtering and sorting logic remains the same
  const filteredGroups = Object.fromEntries(Object.entries(groups).filter(([_, group]) => group.requests.length > 0));

  const sortedGroups = Object.fromEntries(
    Object.entries(filteredGroups).sort(([_, a], [__, b]) =>
      reversed ? b.date.getTime() - a.date.getTime() : a.date.getTime() - b.date.getTime()
    )
  );

  Object.values(sortedGroups).forEach(group => {
    group.requests.sort((a, b) => {
      const dateA = a.comments?.length
        ? new Date(
            a.comments.reduce((latest, comment) => (latest.updatedAt > comment.updatedAt ? latest : comment)).updatedAt
          )
        : new Date(a.updatedAt);
      const dateB = b.comments?.length
        ? new Date(
            b.comments.reduce((latest, comment) => (latest.updatedAt > comment.updatedAt ? latest : comment)).updatedAt
          )
        : new Date(b.updatedAt);

      return reversed ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
    });
  });

  return sortedGroups;
};

export const toDateType = (dateStr: Date | string | undefined): Date | null => {
  if (typeof dateStr !== 'string') {
    return dateStr || null;
  }
  return commonUtil.fromShortString(dateStr);
};
